/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 */

/**
 * @author Satyaki Bose
 * BulkEditModal.jsx
 * Modal component for bulk editing assignment due dates
 */

import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import SyllabusUploadMainComponent from '../../SyllabusUploadMainComponent';
import EmptyStateCard from '../../common/EmptyStateCard';
import useBulkEditStyles from '../styles/BulkEditStyles';
import * as constants from '../../../common/constants';

export default function BulkEditModal({ open,
  onClose,
  clientInformation,
  initialSyllabusInfo,
  onDateAdjusterConfirm,
  onUploadComplete,
  onConfirmClick,
  showTabsinBulkEdit,
  ...props }) {
  const classes = useBulkEditStyles();

  const handleUploadComplete = (result) => {
    // eslint-disable-next-line no-console
    console.log('Bulk edit upload complete:', result);
    onUploadComplete(result);
  };

  const handleDateAdjusterConfirm = (data, isFileUploaded = false) => {
    // eslint-disable-next-line no-console
    console.log('Bulk edit confirm:', data);
    if (isFileUploaded) {
      onConfirmClick(data, false, onClose);
    } else {
      onDateAdjusterConfirm(data);
    }
  };

  const renderBulkEditSubtitle = () => {
    if (!props?.textInfo?.bulkEditSubtitle) {
      return null;
    }

    return (
      <Typography variant="h2" className={classes.bulkEditSubtitle}>{props?.textInfo?.bulkEditSubtitle}</Typography>
    );
  };

  const paperStyle = useMemo(() => {
    if (typeof window === 'undefined') return {};
    const vh = window.innerHeight || constants.BULK_EDIT_MODAL_CONSTANTS.MODAL_MAX_HEIGHT;
    const modalHeight = vh - constants.BULK_EDIT_MODAL_CONSTANTS.MODAL_VERTICAL_PADDING;
    const availableHeight = modalHeight - constants.BULK_EDIT_MODAL_CONSTANTS.RESERVED_NON_TABLE_HEIGHT;
    const tableMax = Math.max(constants.BULK_EDIT_MODAL_CONSTANTS.MIN_TABLE_HEIGHT, availableHeight);

    return { '--table-container-max-height': `${tableMax}px` };
  }, []);

  const LoaderComponent = props.LoaderComponent;
  const showUploadLoader = !props.syllabusUploadEmpty
    && props.isSyllabusUploadLoading
    && LoaderComponent;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        className: classes.bulkEditPaper,
        style: paperStyle
      }}
    >
      <IconButton
        onClick={onClose}
        size="small"
        className={classes.closeIcon}
      >
        <CloseIcon />
      </IconButton>
      <DialogTitle
        classes={{
          root: classes.bulkEditHeader
        }}
      >
        <Typography variant="h2" className={classes.bulkEditTitle}>{props?.textInfo?.bulkEditTitle}</Typography>
      </DialogTitle>
      {renderBulkEditSubtitle()}
      <DialogContent style={{ padding: 0, height: '100%' }}>
        {/* SyllabusUploadMainComponent handles tabs internally */}
        <div className={classes.syllabusUploaderContainer}>
          {showUploadLoader && (
            <div className={classes.loadingOverlay}>
              <LoaderComponent length={8} />
            </div>
          )}
          {props.syllabusUploadEmpty ? (
            <EmptyStateCard
              iconName="BugIcon"
              title={props.textInfo?.uploadNoAssignmentsMessage}
              description={props.textInfo?.uploadNoAssignmentsDescription}
              showActions={!!props.textInfo?.uploadNoAssignmentsRetryCTA}
              retryCTA={props.textInfo?.uploadNoAssignmentsRetryCTA ? {
                text: props.textInfo.uploadNoAssignmentsRetryCTA,
                onClick: props.onUploadEmptyDismiss
              } : null}
            />
          ) : (
            <SyllabusUploadMainComponent
              useChannelsComponent={props.useChannelsComponent}
              getUserCallback={props.getUserCallback}
              clientInformation={clientInformation}
              onUploadComplete={handleUploadComplete}
              onConfirmClick={handleDateAdjusterConfirm}
              onUploadSyllabusClick={props.onConfirmClick}
              onFileError={props.onFileError}
              onUploadSuccess={props.onUploadSuccess}
              onValidationChange={props.onValidationChange}
              config={{
                showTabs: true,
                showAssignments: false,
                mode: constants.BULK_EDIT_MODE,
                showTabsinBulkEdit
              }}
              initialSyllabusInfo={initialSyllabusInfo}
              textInfo={props.textInfo}
              ctaLabels={props.ctaLabels}
              daysOfWeek={props.daysOfWeek}
              tocData={props.tocData}
              assignmentFormActions={props.assignmentFormActions}
              chapterMcqMapping={props.chapterMcqMapping}
              onScreenChange={props.onScreenChange}
              forceListView
              env={props.env}
              locale={props.locale}
              isConfirmPublishInProgress={props.isConfirmPublishInProgress}
              timeZoneOptions={props.timeZoneOptions}
              excludeTitles={props.excludeTitles}
              showAssignmentTitle={props.showAssignmentTitle}
              titleValidationConfig={props.titleValidationConfig}
              syllabusDateCallbacks={props.syllabusDateCallbacks}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

BulkEditModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  clientInformation: PropTypes.object.isRequired,
  initialSyllabusInfo: PropTypes.object,
  onDateAdjusterConfirm: PropTypes.func,
  ctaLabels: PropTypes.object,
  textInfo: PropTypes.object,
  onUploadComplete: PropTypes.func,
  getUserCallback: PropTypes.func.isRequired,
  useChannelsComponent: PropTypes.bool,
  daysOfWeek: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired
    })
  ),
  tocData: PropTypes.arrayOf(PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      id: PropTypes.string.isRequired
    })
  ])).isRequired,
  assignmentFormActions: PropTypes.object,
  chapterMcqMapping: PropTypes.object,
  onConfirmClick: PropTypes.func,
  showTabsinBulkEdit: PropTypes.bool,
  onScreenChange: PropTypes.func,
  onFileError: PropTypes.func,
  onValidationChange: PropTypes.func,
  onUploadSuccess: PropTypes.func,
  isSyllabusUploadLoading: PropTypes.bool,
  LoaderComponent: PropTypes.elementType,
  env: PropTypes.string,
  locale: PropTypes.string,
  isConfirmPublishInProgress: PropTypes.bool,
  timeZoneOptions: PropTypes.array,
  excludeTitles: PropTypes.array,
  showAssignmentTitle: PropTypes.bool,
  titleValidationConfig: PropTypes.object,
  syllabusDateCallbacks: PropTypes.shape({
    normalizeResponseDates: PropTypes.func
  }),
  syllabusUploadEmpty: PropTypes.bool,
  onUploadEmptyDismiss: PropTypes.func
};

BulkEditModal.defaultProps = {
  initialSyllabusInfo: {},
  onDateAdjusterConfirm: () => {},
  ctaLabels: {},
  textInfo: {},
  onUploadComplete: () => {},
  useChannelsComponent: true,
  daysOfWeek: [],
  assignmentFormActions: {},
  chapterMcqMapping: () => {},
  onConfirmClick: () => {},
  showTabsinBulkEdit: false,
  onScreenChange: () => {},
  onFileError: () => {},
  onValidationChange: () => {},
  onUploadSuccess: () => {},
  isSyllabusUploadLoading: false,
  LoaderComponent: null,
  env: '',
  locale: '',
  isConfirmPublishInProgress: false,
  timeZoneOptions: [],
  excludeTitles: [],
  showAssignmentTitle: false,
  titleValidationConfig: {},
  syllabusDateCallbacks: {},
  syllabusUploadEmpty: false,
  onUploadEmptyDismiss: () => {}
};
